import { NgClass } from '@angular/common';
import { Component, inject, OnInit, signal } from '@angular/core';
import { FormsModule, NonNullableFormBuilder, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';

import { FaDuotoneIconComponent, FaIconComponent } from '@fortawesome/angular-fontawesome';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { first, Observable, Subject } from 'rxjs';

import { ClickActionEvent } from '@bn/primitives';
import { SanitizeHtmlPipe } from '@bn/shared/core/pipes/sanitize-html.pipe';

import { ButtonComponent } from '../../../button/button.component';
import { CheckboxComponent } from '../../../checkbox/checkbox.component';
import { ModalState } from '../../../dialog/models/modal-state.model';
import { FontawesomeGradientDirective } from '../../../directives/fontawesome-gradient.directive';
import { MarkAllTouchedDirective } from '../../../directives/mark-all-touched.directive';
import { InputComponent } from '../../../input/input.component';
import { FormcontrolErrorsComponent } from '../../../standalone/formcontrol-errors/formcontrol-errors.component';

@Component({
  templateUrl: './text-input-dialog.component.html',
  imports: [
    FormsModule,
    MarkAllTouchedDirective,
    ReactiveFormsModule,
    FaIconComponent,
    FontawesomeGradientDirective,
    NgClass,
    FaDuotoneIconComponent,
    InputComponent,
    FormcontrolErrorsComponent,
    CheckboxComponent,
    ButtonComponent,
    SanitizeHtmlPipe,
  ],
})
export class TextInputDialogComponent implements OnInit {
  #dialogRef: BsModalRef<TextInputDialogComponent> = inject(BsModalRef);
  #fb = inject(NonNullableFormBuilder);
  #onAction = new Subject<ClickActionEvent>();
  #defaultValidators: ValidatorFn[] = [Validators.required];

  isLoading = signal(false);
  data?: ModalState;
  inputValue?: string;
  inputSettings?: ModalState['input'];
  checkboxChecked = false;
  form = this.#fb.group({
    inputControl: this.#fb.control<string>('', {
      validators: this.#defaultValidators,
    }),
  });

  constructor() {
    this.#dialogRef.onHide?.pipe(first()).subscribe((event) => {
      if (/* event === 'backdrop-click' ||  */ event === 'esc') {
        this.#onAction.next({
          ...this.data?.event,
          action: 'cancel',
        });
      }
    });
  }

  get errorMessage(): string | null {
    const errorMessage = this.data?.extra?.errorMessage as string;
    return errorMessage || null;
  }

  ngOnInit(): void {
    const { input, checkbox } = this.data ?? {};
    const validators = input?.validators || this.#defaultValidators;

    this.inputValue = input?.value?.toString() || '';
    this.inputSettings = input;
    this.checkboxChecked = checkbox?.checked ?? false;
    this.form.patchValue({ inputControl: this.inputValue });
    this.form.get('inputControl')?.setValidators(validators);
  }

  onSubmit(): void {
    if (this.isLoading() || this.form.invalid) return;

    this.isLoading.set(true);
    this.#onAction.next({
      data: {
        ...this.data,
        value: this.form.getRawValue().inputControl.trim(),
        extra: this.data?.checkbox ? { ...this.data?.extra, checkboxChecked: this.checkboxChecked } : this.data?.extra,
      },
      action: 'confirm',
    });
  }

  onCloseDialog(): void {
    this.#onAction.next({ action: 'cancel' });
  }

  getOnAction$(): Observable<ClickActionEvent> {
    return this.#onAction.asObservable();
  }

  setLoading(loading: boolean): void {
    this.isLoading.set(loading);
  }
}

export interface GetOnAction {
  getOnAction$(): Observable<ClickActionEvent>;
}
